import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FileSaver from 'file-saver';
import XLSX, { BookType } from 'sheetjs-style';
import TPOButton from '../TPOButton';

export const EXPORT_BUTTON_TESTID = 'EXPORT_BUTTON_TESTID';
export const EXPORT_POPPER_TESTID = 'EXPORT_POPPER_TESTID';

interface TPOExportButtonProps {
  sheetContent: unknown[];
  sheetHeaders?: string[];
  fileName: string;
  label?: string;
  width?: string;
  dropDownTranslation?: string;
  styles?: CSSProperties;
}

const TPOExportButton = ({ sheetContent, sheetHeaders, fileName, label, width, dropDownTranslation }: TPOExportButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  if (label == undefined) label = t('common.exportTable');
  const defaultWidth = '110px';
  const defaultDropDownTranslation = '37px'
  if (width == undefined) width = defaultWidth;
  if (dropDownTranslation == undefined) dropDownTranslation = defaultDropDownTranslation;

  const exportTable = async (exportType: BookType) => {
    try {
      const isCsv = exportType === 'csv';
      const fileType = isCsv
        ? 'text/csv;charset=utf-8;'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.' + exportType;

      const fullContent = sheetHeaders && sheetHeaders.length > 0
        ? [sheetHeaders, ...sheetContent as any[][]]
        : sheetContent as any[][];

      const worksheet = XLSX.utils.aoa_to_sheet(fullContent);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };

      const writeType = isCsv ? 'string' : 'array';
      const excelBuffer = XLSX.write(workbook, { bookType: exportType, type: writeType });

      const outputData = isCsv ? new Uint8Array(excelBuffer.length) : excelBuffer;
      // wait string to blob is direct
      const data = new Blob([excelBuffer], { type: fileType });

      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      console.error('Failed to export table:', e);
    }
  };

  const handleExportCSV = () => {
    exportTable('csv');
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box>
      <TPOButton
        onClick={handleExportCSV}
        data-testid={EXPORT_BUTTON_TESTID}
        styles={{
          width: 'auto',
          minWidth: width,
          height: 'auto',
          minHeight: '60px',
          padding: '12px 24px',
          borderColor: '#076b58 !important',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}
      >
        <span>{[label]}</span>
        <FileDownloadIcon fontSize="small" />
      </TPOButton>
    </Box>
  );
};

export default TPOExportButton;
